/**
 * The ONE write-outcome channel (WR-685 hotfix D8; WR-687): every document write the
 * user must be TOLD about is reported here exactly once, and handed to the handler the
 * project door installs (`projectManager` → `announceRefusal`, the ONE messenger,
 * WR-675 Δ10). Two outcomes, never both for one write:
 *   - `refused`        — the document write guard refused the write (WR-685: the project
 *                        was changed or deleted in another tab);
 *   - `storage-failed` — the write reached storage and storage failed (WR-687: IndexedDB
 *                        rejected it or is unavailable; there is no fallback tier).
 * A successful document write is reported as `saved`, which ends a storage-failure run.
 *
 * The storage layer cannot import the door (the door imports it), so the door installs
 * its handler at RUNTIME (first create or commit), never at module evaluation (WR-682 /
 * BOOT-TDZ-001). An outcome before any handler exists is still counted and logged.
 *
 * ⛔ No module-scope work beyond declaring the empty handler slot.
 */
import type { WriteRefusalReason } from "./documentWriteGuard";

export type WriteOutcome =
  | { kind: "refused"; reason: WriteRefusalReason }
  | { kind: "storage-failed" }
  | { kind: "saved" };

export type WriteOutcomeHandler = (projectId: string, outcome: WriteOutcome) => void;

let handler: WriteOutcomeHandler | null = null;

/** Installed by the project door (idempotent). */
export function setWriteOutcomeHandler(h: WriteOutcomeHandler | null): void {
  handler = h;
}

function deliver(projectId: string, outcome: WriteOutcome): void {
  try {
    handler?.(projectId, outcome);
  } catch (error) {
    console.error("[WRITE-GUARD] outcome report failed:", error);
  }
}

/**
 * Report ONE refused write: counted (`globalThis.__WRITE_GUARD_REFUSED`), logged once,
 * and handed to the door's messenger. Never throws.
 */
export function reportWriteRefused(
  projectId: string,
  reason: WriteRefusalReason,
  key: string,
): void {
  const g = globalThis as any;
  g.__WRITE_GUARD_REFUSED = (g.__WRITE_GUARD_REFUSED || 0) + 1;
  console.warn(`[WRITE-GUARD] refused: ${reason} ${key}`);
  deliver(projectId, { kind: "refused", reason });
}

/**
 * Report ONE document write that storage failed after its retry (WR-687): counted
 * (`globalThis.__DOCUMENT_SAVE_FAILED`) and handed to the messenger. Never throws.
 */
export function reportStorageFailure(projectId: string, key: string): void {
  const g = globalThis as any;
  g.__DOCUMENT_SAVE_FAILED = (g.__DOCUMENT_SAVE_FAILED || 0) + 1;
  console.error(`[WRITE-GUARD] storage failed: ${key}`);
  deliver(projectId, { kind: "storage-failed" });
}

/** Report ONE successful document write (ends a storage-failure run). Never throws. */
export function reportDocumentSaved(projectId: string): void {
  deliver(projectId, { kind: "saved" });
}
